/**
 * useSimulate — pre-flight dry-run of the swap via eth_call.
 *
 * After the user clicks "Swap", we first simulate the transaction using
 * the public client's `call` method. If the simulation reverts, we
 * decode the error using the AsicExecutor ABI and show the user a
 * friendly message before the wallet ever opens — saving gas on a
 * guaranteed revert.
 *
 * Returns a `simulate` function that resolves to `{ ok: true }` or
 * `{ ok: false, reason: string }`.
 */

import { useCallback } from "react";
import { usePublicClient } from "wagmi";
import type { Address } from "viem";
import { decodeErrorResult } from "viem";
import { friendlyError } from "@/lib/errors";
import { ASIC_EXECUTOR_ABI } from "@/lib/contracts";

interface SimulateResult {
  ok: boolean;
  reason?: string;
}

export function useSimulate() {
  const publicClient = usePublicClient();

  const simulate = useCallback(
    async (
      to: Address,
      data: `0x${string}`,
      from: Address,
    ): Promise<SimulateResult> => {
      if (!publicClient) return { ok: true }; // can't simulate without client
      try {
        await publicClient.call({
          to,
          data,
          account: from,
        });
        return { ok: true };
      } catch (err: unknown) {
        // Try to decode revert data using the AsicExecutor ABI
        const revertData = extractRevertData(err);
        if (revertData) {
          try {
            const decoded = decodeErrorResult({
              abi: ASIC_EXECUTOR_ABI,
              data: revertData,
            });
            // friendlyError checks errorName, so attach it
            return {
              ok: false,
              reason: friendlyError({ errorName: decoded.errorName, message: decoded.errorName }),
            };
          } catch {
            // ABI decode failed — fall through to generic handler
          }
        }
        return { ok: false, reason: friendlyError(err) };
      }
    },
    [publicClient],
  );

  return { simulate };
}

/** Extract hex revert data from a viem CallExecutionError or similar. */
function extractRevertData(err: unknown): `0x${string}` | undefined {
  // viem wraps revert data in err.cause.data or err.data
  const data =
    (err as { data?: string })?.data ??
    (err as { cause?: { data?: string } })?.cause?.data ??
    (err as { cause?: { cause?: { data?: string } } })?.cause?.cause?.data;
  if (typeof data === "string" && data.startsWith("0x") && data.length > 2) {
    return data as `0x${string}`;
  }
  return undefined;
}
